import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { decodeJwtPayload, validateOTP, setSession } from "../../services/authService";
import "./LoginPage.css";
import "./OTPPage.css";

const OTP_LENGTH = 6;

const OTPPage = () => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const token = location.state?.token || "";
  const tokenPayload = decodeJwtPayload(token);
  const email = location.state?.email || tokenPayload?.email || "";
  const name = location.state?.name || tokenPayload?.name || "";

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // digits only
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (otp.every((digit) => digit !== "") && !loading) {
        handleVerify();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { updated[i] = ch; });
    setOtp(updated);
    const nextEmpty = pasted.length < OTP_LENGTH ? pasted.length : OTP_LENGTH - 1;
    inputRefs.current[nextEmpty]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < OTP_LENGTH || loading) return;
    setError("");
    setLoading(true);
    try {
      await validateOTP(token, code);
      await setSession(email, name);
      navigate("/");
    } catch (err) {
      setError(err.message || "Invalid OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleResend = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    inputRefs.current[0]?.focus();
    let seconds = 30;
    setResendTimer(seconds);
    const interval = setInterval(() => {
      seconds -= 1;
      setResendTimer(seconds);
      if (seconds <= 0) clearInterval(interval);
    }, 1000);
  };

  const isComplete = otp.every((d) => d !== "");

  return (
    <div className="otp-page">
      {/* Navbar */}
      <header className="login-navbar">
        <div className="login-navbar-inner">
          <div className="login-logo-container">
            <img src="/AT_FullLogo.png" alt="Alpha Torana" className="login-logo" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="login-main">
        <div className="login-card otp-card">
          <h1 className="login-title">Verify OTP</h1>

          {email && (
            <p className="otp-subtext">
              A 6-digit OTP has been sent to<br />
              <span className="otp-email">{email}</span>
            </p>
          )}

          {/* OTP inputs */}
          <div className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className={`otp-box${digit ? " otp-box--filled" : ""}${error ? " otp-box--error" : ""}`}
                value={digit}
                onChange={(e) => { handleChange(e.target.value, i); setError(""); }}
                onKeyDown={(e) => handleKeyDown(e, i)}
                disabled={loading}
              />
            ))}
          </div>

          {/* Error message */}
          {error && <p className="login-error otp-error">{error}</p>}

          {/* Verify button */}
          <button
            className={`login-btn login-btn-filled otp-verify-btn${(isComplete && !loading) ? "" : " otp-verify-btn--disabled"}`}
            onClick={handleVerify}
            disabled={!isComplete || loading}
          >
            {loading ? "Verifying…" : "Verify OTP"}
          </button>

          {/* Resend */}
          <div className="otp-resend">
            {resendTimer > 0 ? (
              <span className="otp-resend-timer">Resend OTP in {resendTimer}s</span>
            ) : (
              <span>
                Didn't receive the OTP?{" "}
                <button className="otp-resend-btn" onClick={handleResend}>
                  Resend
                </button>
              </span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default OTPPage;
